import React, { Suspense, useEffect, useState } from 'react'
import uuid from 'uuid/v4'

import { axios } from 'api'
import { Flex, Loading, Spinner } from 'ui/bend'
import { UploadProvider } from 'context'

import StyledAdminWrapper from './StyledAdminWrapper'

const Redemption = React.lazy(() =>
  import(
    /* webpackChunkName: 'admin-redemption' */ 'components_v2/Admin/Redemption'
  )
)

// Grab the slug from the path if present (it will be /redemption_offers/{slug}/edit); hence why we grab the second to last element.
const offerSlug = (() => {
  const path = window.location.pathname.split('/')
  if (path.slice(-1).toString() === 'edit') {
    // If we are on an edit page, return the offerSlug located right before "edit"
    return path.slice(-2, -1).toString()
  } else {
    // We are not on an edit page, therefore we do not have an existing offer slug.
    return null
  }
})()

const StyledRedemptionNew = ({ ...rest }) => {
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const formatOffer = (data) => {
      // The API breaks user and admin specific fields into 'standard' (i.e. user-land) and 'supplemental' (i.e. admin-land)
      // Spread these out to make a flat object for Formik's initial values.
      const { carousel: unformattedCarousel, ...offer } = data.standard
      const {
        plans: unformattedPlans,
        ...offerSupplemental
      } = data.supplemental

      const carousel = unformattedCarousel.map((slide) => {
        return {
          ...slide,
          // We need to supply a UUID to each carousel item for React to track the DOM updates for the array appropriately.
          uuid: uuid(),
          // Coerce null values into strings so we can have controlled inputs in React.
          title: slide.title || '',
          subtitle: slide.subtitle || '',
          // If there is no video it returns video:null. We need to change this into the shape we want for Formik.
          video: slide.video || {
            file: null
          }
        }
      })

      const plans = unformattedPlans.map((plan) => {
        const {
          interval,
          intervalCount,
          trialCount,
          trialInterval,
          trialIsPaid
        } = plan

        const discountedPrice = plan.discountedPriceUsCents
          ? plan.discountedPriceUsCents / 100
          : ''
        const recurringPrice = plan.priceUsCents ? plan.priceUsCents / 100 : ''
        const discountedNumber = plan.discountedNumber
          ? plan.discountedNumber
          : '' // we don't want to show 0 default for Discounted Payments field.

        const formattedPlan = {
          discountedNumber,
          interval,
          intervalCount,
          trialCount,
          trialInterval,
          trialIsPaid,
          discountedPrice,
          recurringPrice
        }

        return formattedPlan
      })

      const formattedData = {
        plans,
        carousel,
        offer: {
          ...offer,
          ...offerSupplemental
        }
      }

      // The API returns `null` for blank values if they are not available (e.g. offer.description).
      // Coerce into blank strings so we don't get uncontrollable -> controllable errors.
      for (let elem in formattedData.offer) {
        if (formattedData.offer[elem] === null) formattedData.offer[elem] = ''
      }

      setOffer(formattedData)
      setLoading(false)
    }

    const fetchOffer = async () => {
      try {
        const res = await axios.get(`/redemption_offers/${offerSlug}`, {
          baseURL: '/admin/api/'
        })
        if (res.data) {
          formatOffer(res.data)
        }
      } catch (error) {
        if (error.response.status === 404) {
          setError('There was no offer associated with this slug')
        } else {
          setError('There has been an error, please contact Engineering')
        }
      }
    }

    // If an offer slug exists, we fetch it for editing. Otherwise, load a blank form.
    if (offerSlug) {
      fetchOffer()
    } else {
      setLoading(false)
    }
  }, [])

  return (
    <StyledAdminWrapper>
      <Loading
        loading={loading}
        error={Boolean(error)}
        LoadingComponent={
          <Flex
            height='100vh'
            width={['100%', '75%']}
            alignItems='center'
            justifyContent='center'
          >
            <Spinner />
          </Flex>
        }
        ErrorComponent={<Flex>{error}</Flex>}
      >
        <Suspense fallback=''>
          <UploadProvider>
            <Redemption offerData={offer} {...rest} />
          </UploadProvider>
        </Suspense>
      </Loading>
    </StyledAdminWrapper>
  )
}

export default StyledRedemptionNew
